import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Instagram, Youtube, Facebook, Phone } from 'lucide-react';
import CategoryNav from './CategoryNav';
import FilterChip from './FilterChip';
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import { SOCIAL_FIELDS } from './SocialLinks';

// The platform datatable declares this multiselect as a `string` column, so a
// saved array comes back comma-joined ("Dance Schools,Choreographers") rather
// than as an array. Split it back apart — an `Array.isArray` check fails on a
// string, and a plain `===` compare can never match a multi-value pick.
export function toTypeArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
  return [];
}


export const ARTIST_TYPES = ['Dance Schools', 'Bands', 'Live Singers', 'Dhol', 'Magicians', 'Choreographers'] as const;

const PERFORMING_ARTISTS_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "type", label: "Type", type: "multiselect", options: [...ARTIST_TYPES] },
  { key: "contactUrl", label: "Contact URL (Instagram/Facebook/YouTube)", type: "url", placeholder: "https://…" },
  { key: "socialMediaUrl", label: "Social Media URL", type: "url", placeholder: "https://instagram.com/… or facebook.com/… or youtube.com/…" },
  { key: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 555-5555" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export const PERFORMING_ARTISTS_DATA = [
  {
    id: 'ethnique-performing-arts',
    name: 'Ethnique Performing Arts',
    type: 'Dance Schools',
    contactUrl: 'https://www.instagram.com/ethnique.performing.arts/',
    description: 'Bollywood Dance Classes',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784188579949-56p4x.png',
  },
  {
    id: 'arya-dance-academy',
    name: 'Arya Dance Academy',
    type: 'Dance Schools',
    contactUrl: 'https://www.instagram.com/aryadanceacademytexas/',
    description: 'Bollywood Dance Classes',
    image: '',
  },
  {
    id: 'udance-academy',
    name: 'UDance Academy',
    type: 'Dance Schools',
    contactUrl: 'https://www.instagram.com/udanceacademy_dallas/',
    description: 'Bollywood Dance Classes',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784188722895-cikwdr.png',
  },
  {
    id: 'dholi-tk',
    name: 'Dholi TK',
    type: 'Dhol',
    contactUrl: 'https://www.instagram.com/dholitk/',
    description: 'Drummer Dholi',
    image: '',
  },
  {
    id: 'dholi-eshaan',
    name: 'Dholi Eshaan',
    type: 'Dhol',
    contactUrl: 'https://www.instagram.com/dholi.eshaan/',
    description: 'Dhol Player',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784189258185-v2llep.png',
  },
  {
    id: 'shivam-dhol-tasha-pathak',
    name: 'Shivam Dhol Tasha Pathak',
    type: 'Dhol',
    contactUrl: 'https://www.instagram.com/shivamdallas/',
    description: 'Dhol Tasha',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784189189000-m0reuj.png',
  },
  {
    id: 'harmonic-mixers',
    name: 'Harmonic Mixers',
    type: ['Live Singers', 'Bands'],
    contactUrl: 'https://www.instagram.com/harmonicmixers/',
    description: 'Live Singing and Raw Jamming',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783619442628-dhgsl.png',
  },
  {
    id: 'amitha',
    name: 'Amitha',
    type: 'Live Singers',
    contactUrl: 'https://www.youtube.com/@amithasinger9541',
    description: 'Live Singer',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783441068028-xxykfvb.png',
  },
  {
    id: 'folk-story',
    name: 'Folk Story',
    type: 'Bands',
    contactUrl: 'https://www.instagram.com/official.folkstory/',
    description: 'A Folk-Rock & Bollywood Fusion journey',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783622821625-f00ht7.png',
  },
  {
    id: 'bhajan-unplugged-dallas',
    name: 'Bhajan Unplugged Dallas',
    type: 'Bands',
    contactUrl: 'https://www.instagram.com/bhajanunpluggeddallas',
    description: 'Bhajan jamming using modern instruments',
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-15/888b075a30ea.png',
    location: 'Irving',
  },
  {
    id: 'ashik-prakash',
    name: 'Ashik Prakash',
    type: 'Magicians',
    contactUrl: 'https://www.instagram.com/ashikmagicallyurs',
    description: 'Mentalist and Magician',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784342174173-9x5ja8.jpg',
  },
  {
    id: 'tatva-musical-vibes-llc',
    name: 'Tatva (Musical Vibes LLC)',
    type: 'Bands',
    contactUrl: 'https://www.instagram.com/tatvadallas?igsh=bHFtNTM3N2pxNW5t',
    description: 'Live Band',
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-18/09b199346589.jpeg',
    location: 'Frisco',
  },
  {
    id: 'nennus-dance-academy',
    name: 'Nennus Dance Academy',
    type: 'Dance Schools',
    contactUrl: 'https://www.instagram.com/nennus_dance_academy/',
    description: 'Dance Academy',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784448859776-p8pi5i.png',
  },
  {
    id: 'dance-w-samy',
    name: 'DANCE W SAMY',
    type: 'Dance Schools',
    contactUrl: 'https://www.instagram.com/dancewsamy',
    description: 'Dance Instructor',
    image: '',
  },
  {
    id: 'vaibhav-sheth',
    name: 'Vaibhav Sheth',
    type: 'Live Singers',
    contactUrl: 'https://www.instagram.com/vaibhav_wav',
    description: 'Professional Singer',
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-22/079bb0a94a2f.jpeg',
  },
  {
    id: 'jag-kohli',
    name: 'Jag Kohli',
    type: 'Live Singers',
    contactUrl: 'https://www.instagram.com/thejagkohli/',
    description: 'Live Singer',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1786487130620-53s72a.jpg',
  },
];

const SEED_ARTISTS = PERFORMING_ARTISTS_DATA;

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&h=400&fit=crop';

// Picks the right icon/label/color for a contact URL based on its domain,
// so the big button always matches the platform it actually links to. Falls
// back to the field's own platform label (e.g. from the TikTok/Website admin
// field) when the URL itself doesn't reveal the domain.
function contactButtonMeta(url: string, fallbackLabel?: string) {
  const u = url.toLowerCase();
  if (u.includes('facebook.com')) {
    return { label: 'Facebook', Icon: Facebook, cls: 'bg-[#1877F2] hover:bg-[#166fe0]' };
  }
  if (u.includes('youtube.com') || u.includes('youtu.be')) {
    return { label: 'YouTube', Icon: Youtube, cls: 'bg-[#FF0000] hover:bg-[#e60000]' };
  }
  if (u.includes('instagram.com')) {
    return { label: 'Instagram', Icon: Instagram, cls: 'bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90' };
  }
  if (fallbackLabel === 'TikTok') {
    return { label: 'TikTok', Icon: Instagram, cls: 'bg-black hover:bg-gray-900' };
  }
  if (fallbackLabel === 'Website') {
    return { label: 'Website', Icon: Instagram, cls: 'bg-[#0891b2] hover:bg-[#067a94]' };
  }
  return { label: fallbackLabel || 'Instagram', Icon: Instagram, cls: 'bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90' };
}

export function ArtistCard({ artist, index }: { artist: { name: string; contactUrl?: string; socialMediaUrl?: string; instagram?: string; youtube?: string; image?: string; description?: string; phone?: string }; index: number }) {
  // Every contact/social link an admin fills in (contactUrl, socialMediaUrl,
  // or any of the SOCIAL_FIELDS: instagram/facebook/youtube/tiktok/website)
  // renders as a full branded button here, consistent across all cards —
  // never as a small round icon. Dedup so the same URL entered in more than
  // one field doesn't render twice.
  const a = artist as any;
  const candidates: { url?: string; label?: string }[] = [
    { url: a.contactUrl },
    { url: a.socialMediaUrl },
    { url: a.instagram, label: 'Instagram' },
    { url: a.facebook, label: 'Facebook' },
    { url: a.youtube, label: 'YouTube' },
    { url: a.tiktok, label: 'TikTok' },
    { url: a.website, label: 'Website' },
  ];
  const seen = new Set<string>();
  const contactButtons = candidates
    .filter((c): c is { url: string; label?: string } => !!c.url && c.url !== '#')
    .filter((c) => (seen.has(c.url) ? false : (seen.add(c.url), true)))
    .map((c) => ({ url: c.url, ...contactButtonMeta(c.url, c.label) }));
  const phone = (artist as any).phone as string | undefined;
  const hasCall = !!phone && phone.trim() !== '';
  const totalButtons = contactButtons.length + (hasCall ? 1 : 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col group"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="relative w-full h-36 overflow-hidden bg-gray-50">
        <img
          src={artist.image || FALLBACK_IMAGE}
          alt={artist.name}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="flex flex-col flex-1 px-3 py-3 gap-1.5">
        <h3 className="font-semibold text-gray-900 leading-snug text-[14px]">
          <Link
            to={`/performing-artists/${slugify(artist.name)}`}
            className="hover:text-[#E1306C] transition-colors"
          >
            {artist.name}
          </Link>
        </h3>
        {artist.description && (
          <p className="text-gray-500 text-[12px] leading-relaxed">{artist.description}</p>
        )}
        <div className="mt-auto pt-1.5 flex flex-col gap-2">
          {totalButtons ? (
            <div className={totalButtons > 1 ? 'flex flex-row gap-1.5' : 'flex flex-col'}>
              {contactButtons.map((btn) => (
                <a
                  key={btn.url}
                  href={btn.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`flex-1 flex items-center justify-center gap-1.5 text-white font-semibold rounded-lg transition-opacity duration-200 ${btn.cls} ${
                    totalButtons > 1 ? 'text-[11px] py-1.5 px-1' : 'text-[12px] py-2'
                  }`}
                >
                  <btn.Icon className={totalButtons > 1 ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> {btn.label}
                </a>
              ))}
              {hasCall && (
                <a
                  key="call"
                  href={`tel:${phone!.replace(/[^0-9+]/g, '')}`}
                  onClick={(e) => e.stopPropagation()}
                  className={`flex-1 flex items-center justify-center gap-1.5 bg-[#51ba4f] hover:bg-[#45a443] text-white font-semibold rounded-lg transition-colors duration-200 ${
                    totalButtons > 1 ? 'text-[11px] py-1.5 px-1' : 'text-[12px] py-2'
                  }`}
                >
                  <Phone className={totalButtons > 1 ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> Call
                </a>
              )}
            </div>
          ) : (
            <span className="w-full flex items-center justify-center gap-1.5 bg-gray-50 border border-gray-200 text-gray-400 text-[12px] font-semibold py-2 rounded-lg cursor-not-allowed">
              <Instagram className="w-3.5 h-3.5" /> Contact
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function PerformingArtists() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState<string | null>(null);

  const mc = useManagedCollection<any>("performing_artists", SEED_ARTISTS, (a, i) => slugify(a.name || String(i)));

  // A stale duplicate admin-added row for "Papia Ghose" keeps reappearing even
  // after being deleted from the admin panel (the cross-session override
  // delete doesn't always stick for admin-added "custom" rows — see
  // useManagedCollection notes above). Hard-exclude the duplicate card here:
  // it's identifiable by its longer "...Performer and dance Choreographer"
  // description, distinct from the kept card's shorter description.
  const deduped = mc.items.filter((a) => {
    const name = (a.name || '').toLowerCase().trim();
    const desc = (a.description || '').toLowerCase();
    if (name === 'papia ghose' && desc.includes('choreographer')) return false;
    return true;
  });

  const filtered = deduped.filter((a) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesQuery = !q || a.name.toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q);
    const matchesType = !activeType || toTypeArray(a.type).includes(activeType);
    return matchesQuery && matchesType;
  });

  // Alphabetical order by name, as requested.
  const sorted = [...filtered].sort((a: any, b: any) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .pa-hero-section { height: 185px !important; min-height: 185px !important; }
        .pa-hero-content { height: 185px !important; min-height: 185px !important; }
        @media (max-width: 1023px) { .pa-hero-section, .pa-hero-content { height: 175px !important; min-height: 175px !important; } }
        @media (max-width: 767px) { .pa-hero-section, .pa-hero-content { height: 145px !important; min-height: 145px !important; } }
        @media (max-width: 479px) { .pa-hero-section, .pa-hero-content { height: 140px !important; min-height: 140px !important; } }
      `}</style>

      {/* Hero */}
      <section
        className="pa-hero-section relative w-full"
        style={{ backgroundPosition: 'center center', backgroundSize: 'cover' }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center overflow-hidden"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?w=1440&h=400&fit=crop')" }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/30" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block', zIndex: 3 }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="pa-hero-content relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[600px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col gap-1 sm:gap-0 sm:flex-row bg-transparent sm:bg-white rounded-md sm:shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center bg-white rounded-md sm:rounded-none min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">Find artists</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="e.g. Dance Academy, Performing Arts"
                    className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                  />
                </div>
                <button className="bg-[#E1306C] hover:bg-[#C13584] text-white text-[13px] font-bold px-5 py-2 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] rounded-md sm:rounded-r-md sm:rounded-l-none w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* Secondary Category Nav */}
      <CategoryNav />

      {/* Listings */}
      <section className="bg-gray-50/50 py-8">
        <div className="max-w-[1320px] mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h2 className="text-[18px] sm:text-[20px] md:text-[22px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Performing Artists in Dallas-DFW
            </h2>
            <p className="text-[12px] sm:text-[13px] text-gray-500 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Discover dance academies, performing arts groups, and talented artists across the metroplex.
            </p>

            <div className="mb-6">
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-2.5" style={{ letterSpacing: "0.12em" }}>Explore by Type</p>
              <div className="flex gap-2 flex-wrap items-center">
                {ARTIST_TYPES.map((t) => (
                  <FilterChip key={t} active={activeType === t} color="#E1306C" onClick={() => setActiveType(activeType === t ? null : t)}>{t}</FilterChip>
                ))}
                {activeType && (
                  <button
                    onClick={() => setActiveType(null)}
                    className="text-[11px] font-semibold text-gray-400 hover:text-gray-600 border border-dashed border-gray-300 rounded-full px-3 py-1.5 transition-colors"
                  >
                    ✕ Clear Filter
                  </button>
                )}
              </div>
            </div>

            <AdminAddBar mc={mc} fields={PERFORMING_ARTISTS_FIELDS} label="Add Artist" />

            {sorted.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {sorted.map((artist, index) => (
                  <AdminItem key={artist._key} mc={mc} item={artist} fields={PERFORMING_ARTISTS_FIELDS} title="Artist">
                    <ArtistCard artist={artist} index={index} />
                  </AdminItem>
                ))}
              </div>
            )}

            {sorted.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400 text-[14px]">No artists found for this search.</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
